import { Context, Controller } from '@hotwired/stimulus';
import { humanizeBytes } from 'src/utils/number-humanize';

import type Uppy from '@uppy/core';
import type { UppyFile } from '@uppy/core';
import type { Meta, Body } from 'src/uppy/tar_conversion_direct_upload';
import { emitter } from 'src/emitter';

/**
 * Enable direct file uploads for a file input
 *
 * @example The HTML for a minimal example
 *
 * <div
 *  data-controller="file-direct-upload"
 *  data-file-direct-upload-url-value="<%= rails_direct_uploads_url %>"
 *  data-file-direct-upload-blob-url-template-value="<%= rails_service_blob_url(':signed_id', ':filename') %>">
 *  data-file-direct-upload-representation-url-template-value="<%= representation_url(sgid: ":signed_id", filename: ":filename") %>"
 *    <label>
 *     <span>Upload a file</span>
 *     <input type="file" class="sr-only" data-file-direct-upload-target="input">
 *   </label>
 * </div>
 */
export default class FileDirectUploadController extends Controller {
  static targets = ['label', 'input', 'limit'];
  public static values = {
    url: String,
    blobUrlTemplate: String,
    representationUrlTemplate: String,
    initialValue: String,
    convertArchive: { type: Boolean, default: false },
  };

  private emptyLabel: Node | undefined;

  declare private labelTarget: HTMLLabelElement;
  declare private inputTarget: HTMLInputElement;
  declare private limitTarget: HTMLElement;

  private inputTargetBackup!: HTMLInputElement;

  declare private hasLimitTarget: boolean;

  declare private urlValue: string;
  declare private blobUrlTemplateValue: string;
  declare private representationUrlTemplateValue: string;
  declare private hasInitialValueValue: boolean;
  declare private initialValueValue: string;
  declare private convertArchiveValue: boolean;

  private form: HTMLFormElement | undefined;
  private uppy: Uppy<Meta, Body> | undefined;

  private connected: boolean = false;

  constructor(context: Context) {
    super(context);

    this.onFileChanged = this.onFileChanged.bind(this);

    this.onStartUppy = this.onStartUppy.bind(this);
    this.onFileUploadStarted = this.onFileUploadStarted.bind(this);
    this.onFileUploadFailed = this.onFileUploadFailed.bind(this);
    this.onFileUploaded = this.onFileUploaded.bind(this);
  }

  public connect(): void {
    this.connected = true;
    this.inputTargetBackup = this.inputTarget;

    this.form = this.element.closest('form')!;
    this.inputTarget.addEventListener('click', this.onStartUppy);
    this.inputTarget.addEventListener('change', this.onFileChanged);

    this.emptyLabel = this.labelTarget.cloneNode(true);

    const acceptValue = this.inputTarget.getAttribute('accept');

    void Promise.all([
      import('@uppy/core').then(({ default: Uppy }) => Uppy),
      import('@uppy/dashboard').then(({ default: Dashboard }) => Dashboard),
      import('@uppy/webcam').then(({ default: Webcam }) => Webcam),
      import('src/uppy/tar_conversion_direct_upload').then(
        ({ default: RailsDirectUpload }) => RailsDirectUpload,
      ),
      import('@uppy/screen-capture').then(
        ({ default: ScreenCapture }) => ScreenCapture,
      ),
    ]).then(([Uppy, Dashboard, Webcam, RailsDirectUpload, ScreenCapture]) => {
      if (!this.connected) {
        return;
      }

      this.uppy = new Uppy<Meta, Body>({
        id: `uppy-${this.inputTarget.id}`,
        restrictions: {
          allowedFileTypes: acceptValue
            ?.split(',')
            .map((entry) => entry.trim()),
          maxNumberOfFiles: 1,
          maxFileSize: 1024 * 1024 * 1024,
        },
        debug: true,
        onBeforeFileAdded(currentFile) {
          console.log(currentFile);
          return currentFile;
        },
        onBeforeUpload(files) {
          console.log(files);
          return files;
        },
      })
        .use(Dashboard, {
          inline: false,
          theme: 'auto',
          proudlyDisplayPoweredByUppy: false,
          note: this.translateRestrictions(acceptValue),
        })
        .use(RailsDirectUpload, {
          directUploadUrl: this.urlValue,
          blobUrlTemplate: this.blobUrlTemplateValue,
          representationUrlTemplate: this.representationUrlTemplateValue,
          convertArchive: this.convertArchiveValue,
        });

      // Enable webcam if media is allowed
      if (
        acceptValue?.includes('image') ||
        acceptValue?.includes('video') ||
        acceptValue?.includes('audio')
      ) {
        const modes: (
          'video-audio' | 'video-only' | 'audio-only' | 'picture'
        )[] = [];
        if (acceptValue?.includes('video')) {
          modes.push('video-audio');
          modes.push('video-only');
        }

        if (acceptValue?.includes('image')) {
          modes.push('picture');
        }

        if (acceptValue?.includes('audio')) {
          modes.push('audio-only');
        }

        this.uppy.use(Webcam, {
          modes,
          mirror: false,
          videoConstraints: { facingMode: 'environment' },
          showVideoSourceDropdown: true,
          showRecordingLength: true,
        });
      }

      if (acceptValue?.includes('video')) {
        this.uppy.use(ScreenCapture);
      }

      this.uppy.on('upload', this.onFileUploadStarted);
      this.uppy.on('upload-error', this.onFileUploadFailed);
      this.uppy.on('upload-success', this.onFileUploaded);
    });

    if (this.hasLimitTarget) {
      this.limitTarget.textContent = 'up to 1000 MB';
    }

    if (this.hasInitialValueValue) {
      const newInput = document.createElement('input');

      newInput.type = 'hidden';
      newInput.value = this.initialValueValue;
      newInput.id = this.inputTarget.id;
      newInput.name = this.inputTarget.name;

      for (let i = 0; i < this.inputTargetBackup.attributes.length; i++) {
        const attr = this.inputTargetBackup.attributes[i];

        if (/^data-/.test(attr.nodeName)) {
          newInput.setAttribute(attr.nodeName, attr.value);
        }
      }

      this.labelTarget.addEventListener('click', this.onStartUppy);
      this.inputTarget.replaceWith(newInput);
    }
  }

  private translateRestrictions(accept: string | null): string {
    if (accept === null || !accept) {
      return 'Select 1 file up to 1000 MB';
    }

    const acceptImage = accept?.includes('image/*');
    const acceptVideo = accept?.includes('video/*');
    const acceptSheet = accept?.includes('text/csv');
    const acceptAudio = accept?.includes('audio/*');
    const acceptArchive =
      accept?.includes('application/zip') ||
      accept?.includes('application/x-zip-compressed');
    const acceptTar = accept?.includes('application/x-tar');
    const acceptPdf = accept?.includes('application/pdf');
    const acceptPngJpg =
      accept?.includes('image/png') && accept?.includes('image/jpeg');

    const matches = [
      acceptImage && 'images',
      acceptVideo && 'videos',
      acceptSheet && 'CSVs',
      acceptAudio && 'audios',
      acceptArchive && 'ZIP archives',
      acceptTar && 'TAR archives',
      acceptPngJpg && 'JPGs/PNGs',
      acceptPdf && 'PDFs',
    ].filter(Boolean) as string[];
    if (matches.length === 0) {
      return this.translateRestrictions(null);
    }

    if (matches.length === 1) {
      return `Only ${matches[0]}`;
    }

    const first = matches.shift()!;
    const last = matches.pop();

    return [
      [`${first[0].toLocaleUpperCase() + first.slice(1)}`, ...matches].join(
        ', ',
      ),
      last,
    ].join(' and ');
  }

  public disconnect(): void {
    this.connected = false;

    this.inputTarget.removeEventListener('click', this.onStartUppy);
    this.inputTarget.removeEventListener('change', this.onFileChanged);
    this.labelTarget.removeEventListener('click', this.onStartUppy);

    if (this.uppy) {
      void this.uppy.getPlugin('Dashboard')!.closeModal();

      this.uppy.off('upload', this.onFileUploadStarted);
      this.uppy.off('upload-error', this.onFileUploadFailed);
      this.uppy.off('upload-success', this.onFileUploaded);

      this.uppy = undefined;
    }
  }

  private onFileChanged(): void {
    if (!this.inputTarget || !this.inputTarget.files) {
      return;
    }

    const file = this.inputTarget.files[0];
    if (!file) {
      return;
    }

    // TODO upload
  }

  private onStartUppy(event: Event): void {
    event.preventDefault();

    if (this.uppy) {
      void this.uppy.getPlugin('Dashboard')!.openModal();
    }
  }

  private onFileUploadStarted(): void {}

  private onFileUploadFailed(): void {}

  private onFileUploaded(
    file: UppyFile<Meta, Body> | undefined,
    attributes: {
      body?: Body | undefined;
      status: number;
      bytesUploaded?: number | undefined;
      uploadURL?: string | undefined;
      sgid?: string;
    },
  ): void {
    if (!file) {
      return;
    }

    this.clear();

    const newInput = document.createElement('input');
    newInput.type = 'hidden';
    newInput.value = (
      'sgid' in attributes ? attributes.sgid : attributes.body!.sgid
    )!;
    newInput.id = this.inputTarget.id;
    newInput.name = this.inputTarget.name;

    for (let i = 0; i < this.inputTargetBackup.attributes.length; i++) {
      const attr = this.inputTargetBackup.attributes[i];

      if (/^data-/.test(attr.nodeName)) {
        newInput.setAttribute(attr.nodeName, attr.value);
      }
    }

    this.labelTarget.addEventListener('click', this.onStartUppy);
    this.inputTarget.replaceWith(newInput);

    console.log('[file-direct-upload] new url', attributes.uploadURL);
    if (
      /\bfile-preview\b/.test(
        this.element.getAttribute('data-controller') || '',
      )
    ) {
      console.log(
        '[file-direct-upload] delegating preview to file-preview controller',
      );

      emitter.emit('file-direct-upload:uploaded', {
        element: this.element,
        value: ('sgid' in attributes
          ? attributes.sgid
          : attributes.body!.sgid)!,
        url: attributes.uploadURL,
        file: {
          name: file.name,
          size: file.size,
          type: file.type,
        },
      });
    } else {
      if (file.type?.split('/')[0] === 'image') {
        this.fill(attributes.uploadURL);
      } else {
        this.reference(attributes.uploadURL, file.name, file.size!);
      }
    }
  }

  public clear() {
    // Move the input
    this.element.appendChild(this.inputTargetBackup);

    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const newLabel = this.emptyLabel!.cloneNode(true);

    while (newLabel.lastChild) {
      this.labelTarget.prepend(newLabel.lastChild);
    }

    // Replace the input with the already initialized one
    this.labelTarget
      .querySelector('input[type="file"], input[type="hidden"]')
      ?.replaceWith(this.inputTargetBackup);

    this.labelTarget.removeEventListener('click', this.onStartUppy);
  }

  public fill(url: string | undefined): void {
    if (!url) {
      return;
    }

    // Clear label
    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const image = new Image();
    image.src = url;
    image.classList.add(
      'w-full',
      'h-full',
      'object-center',
      'object-cover',
      'hover:opacity-75',
      'group-hover:opacity-75',
    );

    this.labelTarget.append(image);
  }

  public reference(url: string | undefined, name: string, size: number): void {
    if (!url) {
      return;
    }

    // Clear label
    while (this.labelTarget.lastElementChild) {
      this.labelTarget.removeChild(this.labelTarget.lastElementChild);
    }

    const link = document.createElement('a');
    link.href = url;
    link.target = '__blank';
    link.classList.add(
      'w-full',
      'h-full',
      'hover:opacity-75',
      'group-hover:opacity-75',
    );

    const humanSize = humanizeBytes(size);
    link.textContent = `${name} (${humanSize})`;

    this.labelTarget.append(link);
  }
}
